import { forwardRef, useEffect, useId, useRef, useState, type ReactNode, type HTMLAttributes } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";
import { cn } from "@/lib/cn";

// Stage 5: local replacement for @datasirpi/ui's <NumberInput>. The
// numeric-specific behavior — text/value state sync, format/parse
// (incl. locale grouping), precision rounding, and the +/- step buttons
// — is ported letter-for-letter from the vendor's compiled bundle and its
// NumberInput CSS.
//
// Deliberately NOT ported, matching Input.tsx's exact precedent: the
// declarative `rules`/`validateOn`/`onValidate` validation engine and the
// `NumberInputHandle` imperative ref (`{ validate, element }`). Verified
// zero real call sites anywhere in this app use `rules=`, `validateOn`,
// `onValidate`, or pass a ref to `<NumberInput>` — only the 2 real sites
// in agents/page.tsx exist, using label/min/max/value/onChange/disabled/
// suffix/id. Reimplementing an unused, unverifiable validation engine
// shared with Input/DatePicker is speculative complexity with no call
// site to check it against — the ref this gets instead is a plain
// HTMLInputElement.
export interface NumberInputProps extends Omit<HTMLAttributes<HTMLInputElement>, "onChange" | "defaultValue" | "prefix"> {
  label: ReactNode;
  help?: ReactNode;
  error?: ReactNode;
  required?: boolean;
  value?: number | null;
  defaultValue?: number | null;
  onChange?: (value: number | null) => void;
  min?: number;
  max?: number;
  step?: number;
  precision?: number;
  locale?: string;
  prefix?: ReactNode;
  suffix?: ReactNode;
  hideSteps?: boolean;
  disabled?: boolean;
  placeholder?: string;
}

function format(value: number | null, precision: number | undefined, locale: string | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) return "";
  if (locale) {
    const opts: Intl.NumberFormatOptions = {};
    if (precision !== undefined) {
      opts.minimumFractionDigits = precision;
      opts.maximumFractionDigits = precision;
    }
    return new Intl.NumberFormat(locale, opts).format(value);
  }
  if (precision !== undefined) return value.toFixed(precision);
  return String(value);
}

function parse(text: string, locale: string | undefined): number | null {
  if (text === "" || text === "-" || text === ".") return null;
  if (locale) {
    const parts = new Intl.NumberFormat(locale).formatToParts(12345.6);
    const group = parts.find((p) => p.type === "group")?.value ?? ",";
    const decimal = parts.find((p) => p.type === "decimal")?.value ?? ".";
    const normalized = text.replace(new RegExp(`\\${group}`, "g"), "").replace(decimal, ".");
    const n = Number(normalized);
    return Number.isNaN(n) ? null : n;
  }
  const n = Number(text);
  return Number.isNaN(n) ? null : n;
}

export const NumberInput = forwardRef<HTMLInputElement, NumberInputProps>(function NumberInput(
  {
    label,
    help,
    error,
    required,
    value: controlled,
    defaultValue = null,
    onChange,
    min,
    max,
    step = 1,
    precision,
    locale,
    prefix,
    suffix,
    hideSteps,
    disabled,
    placeholder,
    className,
    id: providedId,
    onKeyDown,
    ...rest
  },
  ref
) {
  const fallbackId = useId();
  const inputId = providedId ?? fallbackId;
  const isControlled = controlled !== undefined;
  const [internalValue, setInternalValue] = useState(defaultValue);
  const numericValue = isControlled ? (controlled ?? null) : internalValue;
  const [text, setText] = useState(() => format(numericValue, precision, locale));
  const inputRef = useRef<HTMLInputElement | null>(null);

  // Re-derive the displayed text when the numeric value (or formatting
  // options) change from outside — but not while the field is focused, so
  // an external update doesn't clobber in-progress typing. This needs an
  // effect rather than the render-time "adjust state" pattern because the
  // skip condition depends on DOM focus, which can only be read as a ref
  // (refs can't be read during render). Ported as-is from the vendor.
  useEffect(() => {
    if (document.activeElement === inputRef.current) return;
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setText(format(numericValue, precision, locale));
  }, [numericValue, precision, locale]);

  function setRef(node: HTMLInputElement | null) {
    inputRef.current = node;
    if (typeof ref === "function") ref(node);
    else if (ref) (ref as React.MutableRefObject<HTMLInputElement | null>).current = node;
  }

  function commit(next: number | null) {
    if (!isControlled) setInternalValue(next);
    onChange?.(next);
  }

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    const raw = e.target.value;
    setText(raw);
    commit(parse(raw, locale));
  }

  function handleBlur() {
    setText(format(numericValue, precision, locale));
  }

  function bumpBy(delta: number) {
    const base = numericValue ?? 0;
    let next = base + delta;
    if (min !== undefined) next = Math.max(min, next);
    if (max !== undefined) next = Math.min(max, next);
    if (precision !== undefined) {
      const factor = 10 ** precision;
      next = Math.round(next * factor) / factor;
    }
    commit(next);
    setText(format(next, precision, locale));
  }

  // d2d-XDR eval F18: the ARIA spinbutton keyboard model. The field had
  // type="text" and no key handling, and the step buttons are (correctly, per
  // the APG pattern) out of the tab order — so keyboard users could not step
  // at all. Arrows step by `step`, PageUp/PageDown by ten steps, Home/End
  // jump to min/max when they are set.
  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    onKeyDown?.(e);
    if (e.defaultPrevented || disabled) return;
    const jump: Record<string, number | undefined> = {
      ArrowUp: step, ArrowDown: -step, PageUp: step * 10, PageDown: -step * 10,
    };
    if (jump[e.key] !== undefined) {
      e.preventDefault();
      bumpBy(jump[e.key]!);
    } else if (e.key === "Home" && min !== undefined) {
      e.preventDefault();
      bumpBy(min - (numericValue ?? 0));
    } else if (e.key === "End" && max !== undefined) {
      e.preventDefault();
      bumpBy(max - (numericValue ?? 0));
    }
  }

  const helpId = help ? `${inputId}-help` : undefined;
  const errorId = error ? `${inputId}-error` : undefined;
  const describedBy = [helpId, errorId].filter(Boolean).join(" ") || undefined;
  const canIncrement = !disabled && (max === undefined || numericValue === null || numericValue < max);
  const canDecrement = !disabled && (min === undefined || numericValue === null || numericValue > min);

  return (
    <div className={cn("rc-numberinput", error && "rc-numberinput--error", className)}>
      <label htmlFor={inputId} className="rc-numberinput__label">
        {label}
        {required ? (
          <span className="rc-numberinput__required" aria-hidden="true">
            *
          </span>
        ) : null}
      </label>
      <div className="rc-numberinput__wrap">
        {prefix ? <span className="rc-numberinput__prefix">{prefix}</span> : null}
        <input
          ref={setRef}
          id={inputId}
          type="text"
          role="spinbutton"
          aria-valuenow={numericValue ?? undefined}
          aria-valuemin={min}
          aria-valuemax={max}
          inputMode={precision !== undefined ? "decimal" : "numeric"}
          required={required}
          disabled={disabled}
          placeholder={placeholder}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy}
          className="rc-numberinput__field"
          value={text}
          onChange={handleChange}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          {...rest}
        />
        {suffix ? <span className="rc-numberinput__suffix">{suffix}</span> : null}
        {!hideSteps ? (
          <div className="rc-numberinput__steps">
            <button
              type="button"
              className="rc-numberinput__step"
              aria-label="Increase value"
              disabled={!canIncrement}
              onClick={() => bumpBy(step)}
              tabIndex={-1}
            >
              <ChevronUp size={12} aria-hidden="true" />
            </button>
            <button
              type="button"
              className="rc-numberinput__step"
              aria-label="Decrease value"
              disabled={!canDecrement}
              onClick={() => bumpBy(-step)}
              tabIndex={-1}
            >
              <ChevronDown size={12} aria-hidden="true" />
            </button>
          </div>
        ) : null}
      </div>
      {help ? (
        <span id={helpId} className="rc-numberinput__help">
          {help}
        </span>
      ) : null}
      {error ? (
        <span id={errorId} className="rc-numberinput__error" role="alert">
          {error}
        </span>
      ) : null}
    </div>
  );
});
